/**
 * Shared admin comments UI: list of { at, by, text } + add form (service-ticket style).
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Section } from './DetailDrawerContent';
import { formatApiDate } from '../utils/formatApiDate';

export function formatEntityCommentDate(value) {
  return formatApiDate(value, 'DD.MM.YYYY HH:mm');
}

/**
 * Normalize entity payload into comments[{at,by,text}].
 * Supports: comments[], commentList[{createdAt,atUser,text}], legacy string comment.
 */
export function normalizeEntityComments(entity) {
  if (!entity) return [];
  if (Array.isArray(entity.comments) && entity.comments.length) {
    return entity.comments.map((c) => ({
      at: c?.at ?? null,
      by: c?.by ?? null,
      text: c?.text ?? '',
    })).filter((c) => c.text != null && String(c.text).trim() !== '');
  }
  if (Array.isArray(entity.commentList) && entity.commentList.length) {
    return entity.commentList.map((c) => ({
      at: c?.createdAt ?? c?.at ?? null,
      by: c?.atUser ?? c?.by ?? null,
      text: c?.text ?? '',
    })).filter((c) => c.text != null && String(c.text).trim() !== '');
  }
  const legacy = entity.comment ?? entity.station?.comment;
  if (legacy != null && String(legacy).trim() !== '') {
    return [{ at: null, by: null, text: String(legacy) }];
  }
  return [];
}

export default function EntityCommentsSection({
  title,
  comments = [],
  canAdd = true,
  busy = false,
  onAdd,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft('');
    setError(null);
  }, [comments]);

  const handleAdd = useCallback(async () => {
    const text = (draft || '').trim();
    if (!text || !onAdd) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd(text);
      setDraft('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t('drawer.comments.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [draft, onAdd, t]);

  const sectionTitle = title || t('drawer.comments.title');

  return (
    <Section title={sectionTitle}>
      <Stack spacing={1} sx={{ mb: canAdd ? 1.5 : 0 }}>
        {(comments || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}
        {(comments || []).map((c, i) => (
          <Paper
            key={`${c.at || 'x'}-${i}`}
            variant="outlined"
            sx={{
              p: 1.25,
              bgcolor: 'background.paper',
              alignSelf: 'stretch',
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {c.by || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatEntityCommentDate(c.at)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
          </Paper>
        ))}
      </Stack>
      {canAdd && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              label={t('drawer.comments.addLabel')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy || saving}
              error={!!error}
              helperText={error || ' '}
              sx={{ flex: 1 }}
              multiline
              minRows={1}
            />
            <Button
              variant="contained"
              size="small"
              disabled={busy || saving || !(draft || '').trim()}
              onClick={handleAdd}
              sx={{ mt: 0.5 }}
            >
              {saving ? t('drawer.saving') : t('drawer.comments.add')}
            </Button>
          </Stack>
        </Box>
      )}
    </Section>
  );
}
